import React, { useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { Badge } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import i18n from '../../../../i18n';

import { Icon } from 'components/Icon/Icon';
import { Text, TextTypography } from 'components/Text/Text';

import { RoutePath } from 'RoutePath';

import { ReactComponent as Logo } from 'assets/logo.svg';
import { ReactComponent as OrdersIcon } from 'assets/glyphs/vendor-menu-orders.svg';
import { ReactComponent as SettingsIcon } from 'assets/glyphs/vendor-menu-settings-shape.svg';
import { ReactComponent as StoreIcon } from 'assets/glyphs/vendor-menu-store.svg';
import { ReactComponent as MenuIcon } from 'assets/icons/main-24/menu-o--24.svg';
import { ReactComponent as DashboardIcon } from 'assets/icons/main-24/pie-chart--24.svg';
import { useAction } from 'core/store/store';
import { makeVendorMenuShowAction } from 'state/vendorMenu/actions';
import { ReactComponent as SubscriptionIcon } from 'assets/icons/main-16/cp-assigned--16.svg';
import { PublishButton } from 'components/PublishButton/PublishButton';

export interface VendorFooterProps {
    hasNewOrders: boolean;
}

const useStyles = makeStyles(theme => ({
    container: {
        // borderBottom: `1px solid ${theme.palette.brandPrimary.accent}`,
        display: 'flex',
        height: theme.spacing(10),
        padding: theme.spacing(0, 3),
        alignItems: 'center',
    },
    left: {
        flex: 1,
    },
    right: {
        flex: 1,
        textAlign: 'right',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center'
    },
    nav: {
        display: 'flex',
        alignContent: 'center',
    },
    navItem: {
        color: theme.palette.fixedPrimary.lighter,
        textAlign: 'center',
        width: theme.spacing(14),
        [theme.breakpoints.down(760)]: {
            width: theme.spacing(11),
        },
    },
    navItemActive: {
        color: theme.palette.fixedPrimary.primary,
        '& $icon': {
            fillOpacity: 1,
        },
    },
    icon: {
        fill: '#243143',
        fillOpacity: 0.4,
    },
    publishButton: {
        marginRight: theme.spacing(4),
        marginBottom: theme.spacing(1),
        [theme.breakpoints.down(960)]: {
            display: 'none',
        },
    },
}));

export const VendorHeader = (props: VendorFooterProps) => {
    const { t } = i18n.useTranslation();
    const classes = useStyles();
    const action = useAction();
    const openVendorMenu = useCallback(() => {
        action(makeVendorMenuShowAction);
    }, []);
    return (
        <div className={classes.container}>
            <div className={classes.left}>
                <Logo />
            </div>
            <div className={classes.nav}>
                <NavLink
                    className={classes.navItem}
                    activeClassName={classes.navItemActive}
                    to={RoutePath.VENDOR_DASHBOARD}
                >
                    <Badge color="error" variant="dot" invisible={true}>
                        <Icon component={DashboardIcon} className={classes.icon} svgSize={24} size={32} />
                    </Badge>
                    <div>
                        <Text typography={TextTypography.CAPTION_ACCENT}>{t('vendor:header.main')}</Text>
                    </div>
                </NavLink>
                <NavLink className={classes.navItem} activeClassName={classes.navItemActive} to={RoutePath.ORDERS}>
                    <Badge color="error" variant="dot" invisible={!props.hasNewOrders}>
                        <Icon component={OrdersIcon} className={classes.icon} svgSize={24} size={32} />
                    </Badge>
                    <div>
                        <Text typography={TextTypography.CAPTION_ACCENT}>{t('vendor:header.orders')}</Text>
                    </div>
                </NavLink>
                <NavLink
                    className={classes.navItem}
                    activeClassName={classes.navItemActive}
                    to={RoutePath.VENDOR_CATALOG}
                >
                    <Badge color="error" variant="dot" invisible={true}>
                        <Icon component={StoreIcon} className={classes.icon} svgSize={24} size={32} />
                    </Badge>
                    <div>
                        <Text typography={TextTypography.CAPTION_ACCENT}>{t('vendor:header.catalog')}</Text>
                    </div>
                </NavLink>
                <NavLink
                    className={classes.navItem}
                    activeClassName={classes.navItemActive}
                    to={RoutePath.VENDOR_MENU_SUBSCRIPTION}
                >
                    <Badge color="error" variant="dot" invisible={true}>
                        <Icon component={SubscriptionIcon} className={classes.icon} svgSize={16} size={32} />
                    </Badge>
                    <div>
                        <Text typography={TextTypography.CAPTION_ACCENT}>{t('vendor:header.subscription')}</Text>
                    </div>
                </NavLink>
                <NavLink
                    className={classes.navItem}
                    activeClassName={classes.navItemActive}
                    to={RoutePath.VENDOR_MENU_STORE_INFO}
                >
                    <Badge color="error" variant="dot" invisible={true}>
                        <Icon component={SettingsIcon} className={classes.icon} svgSize={22} size={34} />
                    </Badge>
                    <div>
                        <Text typography={TextTypography.CAPTION_ACCENT}>{t('vendor:header.settings')}</Text>
                    </div>
                </NavLink>
            </div>
            <div className={classes.right}>
                <div className={classes.publishButton}>
                    <PublishButton withOptions />
                </div>
                <div onClick={openVendorMenu}>
                    <Icon component={MenuIcon} svgSize={24} size={32} />
                </div>
            </div>
        </div>
    );
};
